import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import PlanGrid, { CYCLES, getAvailableCycles, getSaving } from './PlanGrid';
import AutoRenewOption from './AutoRenewOption';
import CheckoutSummary from './CheckoutSummary';
import { startPlanCheckout } from '../../utils/billing';
import { billingAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '';
const COMPANY_PHONE = process.env.REACT_APP_COMPANY_PHONE || '+91 98765 43210';
const COMPANY_EMAIL = process.env.REACT_APP_COMPANY_EMAIL || 'hello@yourcompany.com';

// Full-page, non-dismissable version of the plan grid — shown by PrivateRoute
// in place of any requested page once an org's plan has actually lapsed
// (there's no underlying app to see behind an overlay at that point).
const RenewalRequired = () => {
  const { org, logout, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState('monthly');
  const [selectingPlanId, setSelectingPlanId] = useState(null);
  const [summaryPlan, setSummaryPlan] = useState(null); // plan picked -> show the order summary instead of the cards
  const [activating, setActivating] = useState(false);
  const [autoRenew, setAutoRenew] = useState(true);
  const [discountPercent, setDiscountPercent] = useState(0);

  useEffect(() => {
    axios.get(`${API_URL}/api/public/plans`)
      .then(({ data }) => setPlans(data.plans || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    billingAPI.getSubscription()
      .then(({ data }) => setDiscountPercent(data.firstPurchaseDiscountPercent || 0))
      .catch(() => {});
  }, []);

  const availableCycles = getAvailableCycles(plans);
  const cycleLabel = CYCLES.find((c) => c.key === billing)?.label || billing;

  // Picking a plan opens the order summary (price lines + coupon box); paying happens from there.
  const handleSelectPlan = (plan) => setSummaryPlan(plan);

  const handlePay = async (couponCode, paymentMethod, referralCode) => {
    const plan = summaryPlan;
    setSelectingPlanId(plan.id);
    try {
      await startPlanCheckout({
        planId: plan.id,
        billingCycle: billing,
        autoRenew,
        couponCode,
        referralCode,
        paymentMethod,
        planLabel: `${plan.displayName || plan.name} — ${cycleLabel}`,
        onPaymentReceived: () => {
          setActivating(true);
          toast.success('Payment received — activating your plan…');
        },
        onResult: async (active) => {
          await refreshUser();
          setActivating(false);
          if (active) {
            toast.success('Your plan is active again!');
          } else {
            toast('Payment is processing — this can take a minute. Refresh to check again.', { icon: '⏳' });
          }
        },
        onDismiss: () => setSelectingPlanId(null),
      });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start checkout');
      setSelectingPlanId(null);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.inner}>
        <div style={s.header}>
          <div style={s.eyebrow}>Subscription expired</div>
          <div style={s.title}>Renew to keep using {org?.name || 'your workspace'}</div>
          <div style={s.sub}>Your plan expired{org?.planExpiresAt ? ` on ${new Date(org.planExpiresAt).toLocaleDateString('en-IN')}` : ''}. Choose a plan below to pick up right where you left off — your data is safe and waiting.</div>
          {!summaryPlan && availableCycles.length > 1 && (
            <div style={s.toggle}>
              {availableCycles.map((c) => (
                <button
                  key={c.key}
                  style={{ ...s.toggleBtn, ...(billing === c.key ? s.toggleActive : {}) }}
                  onClick={() => setBilling(c.key)}
                >
                  {c.label}
                  {c.key !== 'monthly' && plans.some((p) => getSaving(p, c)) && (
                    <span style={s.saveBadge}>Save more</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div style={s.spinnerWrap}><div style={s.spinner} /></div>
        ) : summaryPlan ? (
          <CheckoutSummary
            plan={summaryPlan}
            billingCycle={billing}
            cycleLabel={cycleLabel}
            autoRenew={autoRenew}
            onAutoRenewChange={setAutoRenew}
            onBack={() => { setSummaryPlan(null); setSelectingPlanId(null); }}
            onPay={handlePay}
            paying={selectingPlanId !== null || activating}
          />
        ) : (
          <>
            <AutoRenewOption autoRenew={autoRenew} onChange={setAutoRenew} discountPercent={discountPercent} cycleLabel={cycleLabel} />
            <PlanGrid plans={plans} billing={billing} onSelectPlan={handleSelectPlan} selectingPlanId={activating ? null : selectingPlanId} autoRenew={autoRenew} />
          </>
        )}

        <div style={s.footer}>
          Questions? &nbsp;
          <a href={`mailto:${COMPANY_EMAIL}`} style={s.footerLink}>Email us</a>
          &nbsp;or&nbsp;
          <a href={`https://wa.me/${COMPANY_PHONE.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" style={s.footerLink}>Chat on WhatsApp</a>
          &nbsp;·&nbsp;
          <button style={s.logoutLink} onClick={logout}>Log out</button>
        </div>
      </div>
    </div>
  );
};

const s = {
  page: { minHeight: '100vh', background: 'var(--bg, #0d0d1a)', padding: '48px 16px', overflowY: 'auto' },
  inner: { maxWidth: 1200, margin: '0 auto' },
  header: { textAlign: 'center', marginBottom: 32 },
  eyebrow: { fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--danger)', marginBottom: 8 },
  title: { fontSize: 28, fontWeight: 800, color: 'var(--text)', marginBottom: 10 },
  sub: { fontSize: 14, color: 'var(--text-muted)', maxWidth: 560, margin: '0 auto 18px' },
  toggle: { display: 'inline-flex', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 30, padding: 4, gap: 4, flexWrap: 'wrap', justifyContent: 'center' },
  toggleBtn: { background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, fontWeight: 500, padding: '7px 18px', borderRadius: 26, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'inherit' },
  toggleActive: { background: 'linear-gradient(135deg, var(--accent), var(--accent2))', color: '#fff' },
  saveBadge: { fontSize: 10, background: 'var(--success-soft)', color: 'var(--success)', padding: '2px 7px', borderRadius: 20, fontWeight: 700 },
  spinnerWrap: { display: 'flex', justifyContent: 'center', padding: 60 },
  spinner: { width: 36, height: 36, border: '3px solid var(--accent-soft)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'pmSpin 0.8s linear infinite' },
  footer: { textAlign: 'center', fontSize: 13, color: 'var(--text-muted)', paddingTop: 16, borderTop: '1px solid var(--border)' },
  footerLink: { color: 'var(--accent2)', textDecoration: 'underline', cursor: 'pointer' },
  logoutLink: { background: 'none', border: 'none', color: 'var(--text-muted)', textDecoration: 'underline', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', padding: 0 },
};

export default RenewalRequired;
